import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      dialog.addEventListener(DialogOpenEvent.eventName, () => this.#syncCombobox(), { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.#syncCombobox();

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  /**
   * The options the combobox can move through: every role=option inside a visible listbox
   * of the results container, in DOM order.
   * @returns {HTMLElement[]}
   */
  get #allResultsItems() {
    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return [];

    const allItems = Array.from(predictiveSearchResults.querySelectorAll('[role="listbox"] [role="option"]')).filter(
      (item) => item instanceof HTMLElement && !item.closest('[hidden]')
    );

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * The listboxes currently shown in the results container that hold at least one option.
   * @returns {HTMLElement[]}
   */
  get #visibleListboxes() {
    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return [];

    const listboxes = Array.from(predictiveSearchResults.querySelectorAll('[role="listbox"]')).filter(
      (listbox) => listbox instanceof HTMLElement && !listbox.closest('[hidden]') && listbox.querySelector('[role="option"]')
    );

    return /** @type {HTMLElement[]} */ (listboxes);
  }

  /**
   * Keeps the combobox attributes of the search input in step with the rendered results:
   * aria-controls lists the shown listboxes, aria-expanded says whether any is shown, and
   * aria-activedescendant is dropped when the option it named is gone or no longer active.
   */
  #syncCombobox() {
    const { searchInput } = this.refs;
    if (!searchInput) return;

    const listboxes = this.#visibleListboxes;

    listboxes.forEach((listbox, listboxIndex) => {
      if (!listbox.id) listbox.id = `predictive-search-listbox-auto-${listboxIndex}`;
    });

    this.#allResultsItems.forEach((item, itemIndex) => {
      if (!item.id) item.id = `predictive-search-option-auto-${itemIndex}`;
    });

    const hostDialog = this.closest('dialog');
    const isShown = listboxes.length > 0 && (!hostDialog || hostDialog.open);

    if (listboxes.length > 0) {
      searchInput.setAttribute('aria-controls', listboxes.map((listbox) => listbox.id).join(' '));
    } else {
      searchInput.setAttribute('aria-controls', 'predictive-search-results');
    }
    searchInput.setAttribute('aria-expanded', isShown ? 'true' : 'false');

    const activeId = searchInput.getAttribute('aria-activedescendant');
    if (activeId) {
      const activeOption = document.getElementById(activeId);
      if (!isShown || !activeOption || activeOption.getAttribute('aria-selected') !== 'true') {
        searchInput.removeAttribute('aria-activedescendant');
      }
    }
  }

  /**
   * Clears the active option without moving focus, so Tab can leave the field normally.
   */
  #clearActiveOption() {
    for (const item of this.#allResultsItems) {
      item.classList.remove('keyboard-focus');
      item.removeAttribute('aria-selected');
    }
    this.refs.searchInput?.removeAttribute('aria-activedescendant');
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    const { searchInput } = this.refs;

    if (!items.length) {
      searchInput.removeAttribute('aria-activedescendant');
      return;
    }

    /** @type {HTMLElement | null} */
    let activeItem = null;

    for (const [itemIndex, item] of items.entries()) {
      item.classList.remove('keyboard-focus');

      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    if (activeItem) {
      if (!activeItem.id) activeItem.id = `predictive-search-option-auto-${index}`;
      searchInput.setAttribute('aria-activedescendant', activeItem.id);
      activeItem.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }

    searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    /* Result navigation belongs to the search field only. Keys pressed on the reset,
       close or result links keep their native behaviour. */
    if (event.target !== this.refs.searchInput) return;

    /* Tab and Shift+Tab always move focus natively, out of the field. The active option is
       cleared so the field is not left announcing a stale selection. */
    if (event.key === 'Tab') {
      this.#clearActiveOption();
      return;
    }

    if (!this.#allResultsItems.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#updateSearchStatus();
        this.#syncCombobox();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Copy the rendered status text from the morphed-in markup
   * ([data-search-status] span) into the persistent visually-hidden
   * aria-live region in the modal shell (ref="searchStatus"). This
   * pattern keeps the live region in the DOM at all times so screen
   * readers reliably announce result-count changes — including the
   * very first query, which the previous in-section live region did
   * not handle. Closes WCAG 4.1.3 Status Messages (AA).
   */
  #updateSearchStatus() {
    const statusRef = this.refs.searchStatus;
    if (!statusRef) return;

    const sourceNode = this.querySelector('[data-search-status]');
    const message = sourceNode ? sourceNode.textContent.trim() : '';

    // Setting to empty first then to the new value ensures repeat queries
    // with the same count are still announced (some screen readers
    // suppress identical consecutive textContent on a live region).
    statusRef.textContent = '';
    if (message) {
      requestAnimationFrame(() => {
        statusRef.textContent = message;
      });
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    // F-14 (2026-05-12): mirror the aria-expanded flip from #getSearchResults
    // so the combobox state returns to "collapsed" when the input clears.
    searchInput.setAttribute('aria-expanded', 'false');
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    // sections/predictive-search-empty.liquid carries the .predictive-search-empty-section
    // class this looks for. It was deleted in the May 2026 dead-code purge and restored
    // here, because this fetch is its only reference and greps for the filename find
    // nothing. If it goes missing again, degrade rather than throw: an uncaught error
    // here fired on every page load and left the live region holding a stale result
    // count. Reset what we can and skip only the morph.
    if (!parsedEmptySectionMarkup) {
      console.warn('predictive-search: empty-state section markup not found, skipping morph');
      this.#resetScrollPositions();
      this.#updateSearchStatus();
      this.#syncCombobox();
      return;
    }

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      // NH-A11Y-004: ids are title-scoped (predictive-search-products-*), match by prefix.
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.querySelector('[id^="predictive-search-products"]');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('[id^="predictive-search-products"]');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    // Empty-state markup carries no [data-search-status] span, so this
    // clears the persistent live region — preventing the screen reader
    // from re-announcing a stale result count after the user clears the
    // input or closes/reopens the modal.
    this.#updateSearchStatus();
    this.#syncCombobox();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
